import React, { useState, useRef, useEffect } from 'react';
import './style.scss';
import { FormControl, Select, MenuItem, TextField, InputAdornment, Collapse } from '@mui/material';
import CalendarIcon from '../../../../assets/images/CalendarIcon.svg';
import DeleteIcon from '../../../../assets/images/delete.svg';
import AddIcon from '../../../../assets/images/plus-circle.svg';
import ArrowIcon from '../../../../assets/images/angle-down.svg';
import Pencil from '../../../../assets/images/pencil.svg';
import Button from '../../../Shared/components/controls/Button/index';
import Loader from '../../../IBANGeneratorComponent/Loader/index';
import { withTranslation } from 'react-i18next';
import config from '../../../../Config.js';
import { SitecoreContext, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { getCSRFToken, refreshCSRFTokenIfNeeded, clearCSRFToken } from '../../../../utils/csrf';
import { decryptData, validateEncryptionKey } from '../../../../utils/encryptionUtils';

const TripForm = ({
  t,
  tripNumber,
  onDelete,
  expanded,
  onToggle,
  countries,
  setTripData,
  isLastTrip,
  totalTrips,
}) => {
  const [country, setCountry] = useState('');
  const [outboundDate, setOutboundDate] = useState('');
  const [inboundDate, setInboundDate] = useState('');
  const [dateError, setDateError] = useState('');
  const outboundRef = useRef(null);
  const inboundRef = useRef(null);

  const handleCountryChange = (event) => {
    setCountry(event.target.value);
  };

  const handleOutboundClick = () => {
    if (outboundRef.current) {
      outboundRef.current.showPicker();
    }
  };

  const handleInboundClick = () => {
    if (inboundRef.current) {
      inboundRef.current.showPicker();
    }
  };

  const validateDates = (outbound, inbound) => {
    if (outbound && inbound) {
      const outboundDate = new Date(outbound);
      const inboundDate = new Date(inbound);
      if (outboundDate >= inboundDate) {
        setDateError(t('DateValidateErrorMsg'));
      } else {
        setDateError('');
      }
    } else {
      setDateError('');
    }
  };

  const handleOutboundChange = (e) => {
    const newOutboundDate = e.target.value;
    setOutboundDate(newOutboundDate);
    validateDates(newOutboundDate, inboundDate);
    setTripData(tripNumber, { Country: country, Inbound: inboundDate, Outbound: newOutboundDate });
  };

  const handleInboundChange = (e) => {
    const newInboundDate = e.target.value;
    setInboundDate(newInboundDate);
    validateDates(outboundDate, newInboundDate);
    setTripData(tripNumber, { Country: country, Inbound: newInboundDate, Outbound: outboundDate });
  };

  const CustomCalendarIcon = () => (
    <img src={CalendarIcon} alt="Calendar" className="customCalendarIcon" />
  );

  return (
    <div className={`tripContainer ${expanded ? 'expanded' : 'collapsed'}`}>
      <div className="tripHeader" onClick={onToggle}>
        <div className="TwoTitleContainer">
          <span className={expanded ? 'twoTitle' : 'twoTitleCollapsed'}>
            {t('InternationalTrip')} {tripNumber}
          </span>
        </div>
        <img src={Pencil} alt="Toggle" className={`toggleArrow ${expanded ? 'expanded' : ''}`} />
      </div>
      <Collapse in={expanded}>
        <div className="tripContent">
          <div className="destinationContainer">
            <span className="destinationTitle">{t('Destination')}</span>
            <FormControl fullWidth>
              <Select
                value={country}
                onChange={handleCountryChange}
                displayEmpty
                inputProps={{ 'aria-label': 'Without label' }}
                className="countrySelect"
              >
                <MenuItem value="" disabled>
                  {t('Country')}
                </MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country} value={country} style={{ fontWeight: 700 }}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="travelDatesContainer">
            <span className="travelDatesTitle">{t('TravelDates')}</span>
            <div className="datePickersContainer">
              <div className="datePickerWrapper" onClick={handleInboundClick}>
                <TextField
                  label={t('Outbound')}
                  type="date"
                  placeholder={t('Outbound')}
                  value={outboundDate}
                  onChange={handleOutboundChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    inputProps: {
                      min: new Date().toISOString().split('T')[0],
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <CustomCalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                  className="customDatePicker"
                  inputRef={inboundRef}
                />
              </div>
              <div className="datePickerWrapper" onClick={handleOutboundClick}>
                <TextField
                  label={t('Inbound')}
                  placeholder={t('Inbound')}
                  type="date"
                  value={inboundDate}
                  onChange={handleInboundChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    inputProps: {
                      min: new Date().toISOString().split('T')[0],
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <CustomCalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                  className="customDatePicker"
                  inputRef={outboundRef}
                />
              </div>
            </div>
            {dateError && <span className="dateError">{dateError}</span>}
          </div>
          {isLastTrip && totalTrips > 1 && (
            <div className="deleteTrip" onClick={onDelete}>
              <span>{t('DeleteTrip')}</span>
              <img src={DeleteIcon} alt="delete" className="deleteIcon" />
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
};

const StepTwo = ({
  t,
  setActiveStep,
  countriesEndpoint,
  nationalId,
  passportNumber,
  creditCardLastFour,
  extraUserData,
  setExtraUserData,
  generateOTPResponse,
  setGenerateOTPResponse,
  ExtraUserDetailsEndpoint,
  IsMockExtraUserDetailsEndpoint,
  GenerateOTPEndpoint,
  IsMockGenerateOTPEndpoint,
  GenerateOTPToken,
  trips,
  i18n,
  setTrips,
  guid,
  setGuid,
}) => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [extraUserDataError, setExtraUserDataError] = useState(null);
  const [generateOTPResponseError, setGenerateOTPResponseError] = useState(null);
  const [showError, setShowError] = useState(true);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const endpointUrl = `${config.hostname}${countriesEndpoint}`;

        const response = await fetch(endpointUrl);

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        setCountries(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  const setTripData = (tripNumber, tripData) => {
    setTrips((prevTrips) =>
      prevTrips.map((trip) => (trip.id === tripNumber ? { ...trip, ...tripData } : trip))
    );
  };

  const addTrip = () => {
    if (trips.length >= 3) {
      return;
    }
    const newTripId = trips.length + 1;
    setTrips((prevTrips) => [
      ...prevTrips.map((trip) => ({ ...trip, expanded: false })),
      { id: newTripId, expanded: true },
    ]);
  };

  const deleteTrip = (id) => {
    setTrips((prevTrips) => prevTrips.filter((trip) => trip.id !== id));
  };

  const deleteLastTrip = () => {
    setTrips((prevTrips) => {
      if (prevTrips.length === 0) return prevTrips;
      return prevTrips.slice(0, -1);
    });
  };

  const toggleTrip = (id) => {
    setTrips((prevTrips) =>
      prevTrips.map((trip) => ({
        ...trip,
        expanded: trip.id === id ? !trip.expanded : false,
      }))
    );
  };

  const GetExtraUserDetails = async (referenceNumber, isPassportNumber) => {
    try {
      // Get the API host
      const apiHost = config.sitecoreApiHost || config.hostname || window.location.origin;

      // Ensure we have a valid CSRF token
      await refreshCSRFTokenIfNeeded(apiHost);
      const csrfToken = getCSRFToken();

      if (!csrfToken) {
        throw new Error('CSRF token not available. Please refresh the page and try again.');
      }

      const url = `${apiHost}${ExtraUserDetailsEndpoint}`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Important for cookies
        headers: {
          'Content-Type': 'application/json',
          __RequestVerificationToken: csrfToken,
        },
        body: JSON.stringify({
          ReferenceNumber: referenceNumber,
          IsPassportNumber: isPassportNumber,
          Lang: i18n.language,
          IsMock: IsMockExtraUserDetailsEndpoint || false,
        }),
      });

      // Handle CSRF token validation failure
      if (response.status === 403) {
        console.warn('CSRF validation failed. Clearing token...');
        clearCSRFToken();
        throw new Error('Security validation failed. Please refresh the page and try again.');
      }

      if (response.status === 401) {
        throw new Error('Authentication required. Please refresh the page and try again.');
      }

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status} ${response.statusText}`);
      }

      const encryptedResponse = await response.json();

      if (
        encryptedResponse.Data &&
        encryptedResponse.Data.CipherText &&
        encryptedResponse.Data.IV
      ) {
        //  console.log('Encrypted response received, decrypting...');

        try {
          // Decrypt the data using key from config
          const decryptedData = await decryptData(encryptedResponse.Data);
          // console.log('Data decrypted successfully');

          // console.log('extra user data:', decryptedData);

          setExtraUserData(decryptedData); // Set the user data to state
          return decryptedData; // Return data for further processing
        } catch (decryptError) {
          // console.error('Decryption error:', decryptError);
          throw new Error('Failed to decrypt response data. Please try again or contact support.');
        }
      } else {
        // Handle non-encrypted error responses
        // console.log('Non-encrypted response:', encryptedResponse);

        if (encryptedResponse.Message) {
          throw new Error(encryptedResponse.Message);
        }

        // Fallback: if response has Data but no encryption, use it directly (backward compatibility)
        if (encryptedResponse.Data || encryptedResponse.StatusCode) {
          //  console.log('extra user data:', encryptedResponse);
          setExtraUserData(encryptedResponse); // Set the user data to state
          return encryptedResponse; // Return data for further processing
        }

        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      // console.error('Error in GetExtraUserDetails:', error);

      // Set user-friendly error messages
      if (error.message.includes('CSRF') || error.message.includes('Security')) {
        setExtraUserDataError(
          t('SecurityErrorMsg') || 'Security validation failed. Please refresh the page.'
        );
      } else if (error.message.includes('Authentication')) {
        setExtraUserDataError(
          t('AuthenticationErrorMsg') || 'Authentication required. Please refresh the page.'
        );
      } else {
        setExtraUserDataError(t('GenericErrorMsg') || error.message);
      }

      throw error; // Rethrow to handle in the calling function
    }
  };

  const GenerateOTP = async (customerNo) => {
    try {
      setShowError(true);
      const url = `${config.hostname}${GenerateOTPEndpoint}?mock=${IsMockGenerateOTPEndpoint}&reference=${customerNo}&token=${GenerateOTPToken}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to generate OTP');
      }

      const data = await response.json();
      if (data?.output?.success === '1') {
        setGenerateOTPResponse(data);
        return data;
      } else {
        setGenerateOTPResponseError(t('otpGenerationFailed'));
        return null;
      }
    } catch (error) {
      setGenerateOTPResponseError(error.message);
      throw error;
    }
  };

  const handleGenerateOTP = async () => {
    const isPassportNumber = !!passportNumber;

    setLoading(true);
    setExtraUserDataError(null); // Clear previous errors
    setGenerateOTPResponseError(null); // Clear previous errors

    try {
      const extraUserDataResponse = await GetExtraUserDetails(guid, isPassportNumber);

      if (extraUserDataResponse && extraUserDataResponse.ErrorMessage === 'SUCCESS') {
        // console.log('Extra User Details response:', extraUserDataResponse.CustomerNo);
        setGuid(extraUserDataResponse.guid);

        const otpResponse = await GenerateOTP(extraUserDataResponse.CustomerNo);
        //console.log(`Generate OTP Response:`, otpResponse);

        if (otpResponse?.output?.success === '1') {
          setActiveStep(3);
        } else {
          // console.warn('Failed to generate OTP');
        }
      }
    } catch (error) {
      console.error('Error generating OTP:', error);
      // Error messages are already set in GetExtraUserDetails or GenerateOTP
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowError(false);
    }, 120000); // 120 seconds

    return () => clearTimeout(timer);
  }, [showError]);

  if (loading) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  }

  return (
    <div className="stepTwoContainer">
      <div className="travelDetails">
        <div className="headerContainer">
          <span className="detailsTitle">{t('TravelDetails')}</span>
          <span className="detailSubTitle">{t('detailSubTitle')}</span>
        </div>
        {trips.map((trip, index) => (
          <TripForm
            key={trip.id}
            t={t}
            tripNumber={index + 1}
            onDelete={deleteLastTrip}
            expanded={trip.expanded}
            onToggle={() => toggleTrip(trip.id)}
            countries={countries}
            setTripData={setTripData}
            isLastTrip={index === trips.length - 1}
            totalTrips={trips.length}
          />
        ))}
        {trips.length < 3 && (
          <div className="addTripContainer" onClick={addTrip}>
            <div className="addTextContainer">
              <span className="addTitle">{t('AddAnotherTrip')}</span>
            </div>
            <img src={AddIcon} alt="add" />
          </div>
        )}
        <div>
          {showError && extraUserDataError && (
            <div className="error-message">{extraUserDataError}</div>
          )}
          {showError && generateOTPResponseError && (
            <div className="error-message">{generateOTPResponseError}</div>
          )}
        </div>
      </div>
      <div className="btnContainer">
        <Button variant="outlined" onClick={() => setActiveStep(1)}>
          {t('Previous')}
        </Button>
        <Button onClick={() => handleGenerateOTP()}>{t('Next')}</Button>
      </div>
    </div>
  );
};

export default withTranslation()(withSitecoreContext()(StepTwo));
